import { alpha, Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { forwardRef, type ReactNode } from "react";
import { FRAME_PERIOD_SLOT_MIN_HEIGHT } from "src-next/modules/clinical-insights/components/page/FrameAnimatedPeriodControl";
import { insightActionGuideBoxSx } from "./InsightActionGuide";
import { FRAME_BG_ALPHA, PANEL_PADDING, analysisBg } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";

const FRAME_MIN_HEIGHT = 560;
const LARGE_CONTENT_BOTTOM_PADDING = 24;

interface AnalysisDetailFrameProps {
  accentColor: string;
  analysisName: string;
  filteredPatientCount: number;
  totalPatientCount: number;
  scrollParentContentHeight?: number;
  isBusy?: boolean;
  busyMetaText?: string;
  /** Guia com chips de ação (ex.: condutas); omitir em análises sem esse fluxo. */
  actionGuide?: ReactNode;
  /** Controle de período compacto (centro do cabeçalho; slot reserva altura fixa). */
  periodControl?: ReactNode;
  children: ReactNode;
}

export const AnalysisDetailFrame = forwardRef<HTMLDivElement, AnalysisDetailFrameProps>(
  function AnalysisDetailFrame(
    {
      accentColor,
      analysisName,
      filteredPatientCount,
      totalPatientCount,
      scrollParentContentHeight = 0,
      isBusy = false,
      busyMetaText,
      actionGuide,
      periodControl,
      children,
    },
    ref
  ) {
    const patientMeta =
      totalPatientCount > 0
        ? `${filteredPatientCount} de ${totalPatientCount} pacientes`
        : "— pacientes";

    const metaText = isBusy && busyMetaText ? busyMetaText : patientMeta;

    const computedHeight =
      scrollParentContentHeight > FRAME_MIN_HEIGHT
        ? scrollParentContentHeight - LARGE_CONTENT_BOTTOM_PADDING
        : undefined;

    return (
      <Box
        ref={ref}
        sx={{
          mt: 0.5,
          width: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: computedHeight,
          minHeight: computedHeight ? undefined : FRAME_MIN_HEIGHT,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.5, flexShrink: 0 }}>
          <Box
            sx={{
              width: 3,
              height: 20,
              bgcolor: accentColor,
              borderRadius: 1,
              opacity: 0.45,
            }}
          />
        </Box>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: alpha(accentColor, 0.28),
            borderBottom: `3px solid ${accentColor}`,
            borderRight: `4px solid ${accentColor}`,
            borderRadius: 2,
            bgcolor: analysisBg(accentColor, FRAME_BG_ALPHA),
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isBusy ? (
            <LinearProgress
              sx={{
                height: 3,
                bgcolor: alpha(accentColor, 0.12),
                "& .MuiLinearProgress-bar": { bgcolor: accentColor },
              }}
            />
          ) : null}
          <Box
            sx={{
              px: PANEL_PADDING,
              pt: 2,
              pb: 1.25,
              borderBottom: "1px solid",
              borderColor: alpha(accentColor, 0.15),
              bgcolor: analysisBg(accentColor, 0.08),
              flexShrink: 0,
            }}
          >
            <Stack spacing={0.75}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                  alignItems: "center",
                  columnGap: 1,
                  rowGap: 0.5,
                  minHeight: 48,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ minWidth: 0, justifySelf: "start" }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: accentColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {analysisName}
                  </Typography>
                </Stack>
                <Box sx={{ justifySelf: "center", width: "100%", maxWidth: 360, minWidth: 0 }}>
                  {periodControl ?? (
                    <Box sx={{ minHeight: FRAME_PERIOD_SLOT_MIN_HEIGHT, width: "100%" }} />
                  )}
                </Box>
                <Typography
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: "1rem", justifySelf: "end", textAlign: "right", minWidth: 0 }}
                >
                  {metaText}
                </Typography>
              </Box>
              {actionGuide ? (
                <Box sx={insightActionGuideBoxSx(accentColor)}>{actionGuide}</Box>
              ) : null}
            </Stack>
          </Box>
          <Box sx={{ p: PANEL_PADDING, pt: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {children}
          </Box>
        </Paper>
      </Box>
    );
  }
);
